/**
 * @fileoverview system-web Parcel Data admin service — Dexie-backed local-first
 * service for the cross-tenant Regrid parcel data management UI (Phase 7 + 8).
 * @module system-web/services/parcelDataAdmin
 *
 * Responsibilities:
 * - Provide pure Dexie reads for tenant update-token entitlements (from the
 *   `tenants` table — the `updateTokens` field is persisted by Phase 8 routes).
 * - Provide pure Dexie reads for Regrid master-cache GeoParquet file metadata
 *   (from the `geo_cache_files` table, synced via the standard system pull pipeline).
 * - Provide pure Dexie reads for automated import history (from the
 *   `import_runs` table, synced via the standard system pull pipeline).
 * - Provide an inline-edit write for tenant update-token entitlements
 *   (write-through: PUT /api/system/departments/:id/update-tokens → Dexie put).
 * - Provide an admin-bypass update that records a parcel data update on the
 *   tenant document WITHOUT consuming a token (admin updates bypass tokens).
 * - Emit telemetry for the manual "Refresh cache files" action.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, getSystemDexieRaw)
 * - ../apiClient (apiFetch — for online cache file refresh + entitlement writes)
 * - ./telemetry (App Insights tracking)
 *
 * Architecture:
 * - `tenants` and `import_runs` are standard synced collections (pull pipeline).
 * - `geo_cache_files` is a standard synced collection — cache file metadata is
 *   pulled from the system MongoDB `geoCacheFiles` collection via the standard
 *   sync pipeline and kept up-to-date for `liveQuery`.
 * - Read functions are liveQuery-safe (pure Dexie reads, never write).
 * - refreshCacheFiles/setTenantTokenEntitlement/adminBypassUpdate write to Dexie
 *   and MUST be called from a separate effect/handler, never from inside a
 *   liveQuery observed function.
 *
 * Notes:
 * - The `updateTokens` field on tenant documents is persisted by Phase 8 routes
 *   (PUT /api/system/departments/:id/update-tokens). The service reads it
 *   defensively (optional field) and the UI shows "No token entitlements
 *   configured" for tenants without the field.
 * - No mock data; all data comes from Dexie or the system-api endpoint.
 *
 * @localFirst
 * Source of Truth: Dexie for reads; server is authoritative for all writes.
 *   `geo_cache_files` is pull-synced from the system `geoCacheFiles` collection.
 *   Tenant token entitlements are server-authoritative (system DB `tenants`).
 * Write Flow: cache upload placeholders are local-only until the upload route
 *   creates the server record; workers own progress and terminal writes. Tenant
 *   token entitlements use service -> apiFetch (PUT) -> Dexie put; liveQuery re-emits.
 * Sync Strategy: pull-only for `geo_cache_files`; standard sync for `tenants`
 *   and `import_runs`; write-through for entitlement edits.
 * Conflict Resolution: server-authoritative for all data; the pull pipeline
 *   reconciles any divergence on the next sync cycle.
 */

import { getSystemDb, getSystemDexieRaw, wakeLocalWrite, isProtectedOptimisticRow } from '@firesquirrel/local-db';
import { isParcelImportRun } from '@firesquirrel/ui-admin';
import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract TenantTokenEntitlement
 * Purpose: One tenant's update-token entitlement row for the admin table.
 * Fields:  _id — tenantId; tenantName; tokensPerYear; tokensUsed; renewalStart;
 *          renewalEnd; currentVersion; lastUpdateDate.
 * Rules:   `updateTokens` is persisted by Phase 8 routes
 *          (PUT /api/system/departments/:id/update-tokens). The service
 *          projects defaults when the field is missing so the UI can render
 *          the table structure for un-provisioned tenants.
 * Sync:    Read from the `tenants` Dexie table (standard pull pipeline).
 */
export interface TenantTokenEntitlement {
  _id: string;
  tenantName: string;
  tokensPerYear: number;
  tokensUsed: number;
  renewalStart: string | null;
  renewalEnd: string | null;
  currentVersion: string | null;
  lastUpdateDate: string | null;
}

/**
 * @contract GeoCacheFile
 * Purpose: One Regrid master-cache GeoParquet file metadata row, synced from
 *          the system MongoDB geoCacheFiles collection.
 * Fields:  _id — county FIPS code (the stable identity of the county record);
 *          blobUri; manifestBlobUri; filename; state; stateName; county;
 *          fipsCode; featureCount; sizeBytes; compression; dataQuality;
 *          versionDate; sourceFile; hasBboxColumns; spatiallySorted;
 *          status; message; progress; error; uploadJobId; createdAt; updatedAt;
 *          __optimistic / __uploadJobId (internal); fetchedAt (legacy — no longer written,
 *          retained for rows created before the diff-based refresh).
 * Rules:   Server-authoritative. Created at upload time with status='pending'.
 *          Updated in place by the worker as it runs, succeeds, or fails.
 *          A re-upload for the same county overwrites the same record.
 *          Synced to Dexie via the standard pull pipeline. NOT pushed from the client.
 *          Soft-deleted rows carry `_deleted: true` + `deletedAt` + `deletedBy`
 *          (G-020 — user-initiated deletes use tombstones so the delta sync
 *          pull propagates the deletion to all clients).
 * Sync:    Standard system sync pull (geo_cache_files Dexie table).
 */
export interface GeoCacheFile {
  _id: string;
  blobUri: string;
  manifestBlobUri?: string;
  filename: string;
  targetBlobPath?: string;
  state: string;
  stateName?: string;
  county: string;
  fipsCode: string;
  featureCount?: number;
  sizeBytes: number;
  compression?: string;
  versionDate: string | null;
  dataQuality: {
    ownerNamePercent: number | null;
    mailingAddressPercent: number | null;
    situsAddressPercent: number | null;
    geometryValidPercent: number | null;
  } | null;
  sourceFile?: {
    originalName: string;
    originalSizeBytes: number;
    hash: string;
    receivedAt: string;
  };
  hasBboxColumns?: boolean;
  spatiallySorted?: boolean;
  /** Pending / running / completed / failed / cancelled. */
  status?: string;
  /** Human-readable status message. */
  message?: string;
  /** End-to-end progress. Browser upload occupies 0-14%; worker processing occupies 15-100%. */
  progress?: {
    percent: number;
    phase?: string;
    stagePercent?: number;
  };
  /** Error details when status === 'failed'. */
  error?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Soft-delete tombstone flag (G-020). True when the row was user-deleted. */
  _deleted?: boolean;
  /** Timestamp of the soft-delete (G-020). */
  deletedAt?: string;
  /** Actor who soft-deleted the row (G-020) — `'system-web'` locally, the user oid server-side. */
  deletedBy?: string;
  /** Internal: true for rows written optimistically before the server confirms the upload. */
  __optimistic?: boolean;
  /** Server job id for the current upload attempt. */
  uploadJobId?: string;
  /** Internal: accepted job id used to reject stale prior-attempt pulls. */
  __uploadJobId?: string;
  /** Internal: timestamp added by refreshCacheFiles() to distinguish pulled rows. */
  fetchedAt?: string;
}

/**
 * @contract ImportHistoryEntry
 * Purpose: One row in the automated import history table.
 * Fields:  _id; tenantId; tenantName; date; parcels; newCount; updatesCount;
 *          duration; version; dataQuality (per-import coverage percentages).
 * Rules:   Read from the `import_runs` Dexie table (standard pull pipeline)
 *          or projected from the tenant document's regridImportStatistics.
 * Sync:    Synced via the standard system pull pipeline.
 */
export interface ImportHistoryEntry {
  _id: string;
  tenantId: string | null;
  tenantName: string | null;
  date: string;
  parcels: number | null;
  newCount: number | null;
  updatesCount: number | null;
  duration: string | null;
  version: string | null;
  source: 'onboarding' | 'geocache' | 'import';
  dataQuality: {
    ownerNamePercent: number | null;
    mailingAddressPercent: number | null;
    situsAddressPercent: number | null;
    geometryValidPercent: number | null;
  } | null;
}

function resolveTenantFips(tenant: Record<string, unknown>): string | null {
  const parcelImport = tenant.parcelImport as Record<string, unknown> | undefined;
  const jurisdiction = tenant.jurisdiction as Record<string, unknown> | undefined;
  const county = jurisdiction?.county as Record<string, unknown> | undefined;
  const candidates = [parcelImport?.regridFileId, jurisdiction?.fipsCode, county?.code];
  return candidates.find((value): value is string => typeof value === 'string' && /^\\d{5}$/.test(value)) ?? null;
}

/**
 * @reusable canonicalJsonStringify
 * Usage: Comparing two server-sourced objects for content equality without
 *        being sensitive to key insertion order.
 * Rules: Presentation-only — recursively sorts object keys before serializing.
 */
function canonicalJsonStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalJsonStringify).join(',') + ']';
  const sortedKeys = Object.keys(value as Record<string, unknown>).sort();
  return '{' + sortedKeys.map((k) => JSON.stringify(k) + ':' + canonicalJsonStringify((value as Record<string, unknown>)[k])).join(',') + '}';
}

function projectRegridStatistics(
  tenant: Record<string, unknown>,
): ImportHistoryEntry | null {
  const stats = tenant.regridImportStatistics as Record<string, unknown> | undefined;
  if (!stats || typeof stats.importedAt !== 'string') return null;

  const tenantId = typeof tenant._id === 'string' ? tenant._id : null;
  if (!tenantId) return null;

  const dq = stats.dataQuality as Record<string, unknown> | undefined;
  return {
    _id: `regrid-${tenantId}-${stats.importedAt}`,
    tenantId,
    tenantName: typeof tenant.departmentName === 'string'
      ? tenant.departmentName
      : typeof tenant.name === 'string' ? tenant.name : null,
    date: stats.importedAt,
    parcels: typeof stats.districtParcelsMatched === 'number' ? stats.districtParcelsMatched : null,
    newCount: typeof stats.newParcelsAdded === 'number' ? stats.newParcelsAdded : null,
    updatesCount: typeof stats.ownershipChangesDetected === 'number' ? stats.ownershipChangesDetected : null,
    duration: typeof stats.importDurationSeconds === 'number' ? `${stats.importDurationSeconds}s` : null,
    version: typeof stats.azureBlobVersionId === 'string' ? stats.azureBlobVersionId : null,
    source: 'onboarding',
    dataQuality: dq ? {
      ownerNamePercent: typeof dq.ownerNamePercent === 'number' ? dq.ownerNamePercent : null,
      mailingAddressPercent: typeof dq.mailingAddressPercent === 'number' ? dq.mailingAddressPercent : null,
      situsAddressPercent: typeof dq.situsAddressPercent === 'number' ? dq.situsAddressPercent : null,
      geometryValidPercent: typeof dq.geometryValidPercent === 'number' ? dq.geometryValidPercent : null,
    } : null,
  };
}

function projectCacheFileForTenant(
  tenant: Record<string, unknown>,
  cacheFile: Record<string, unknown>,
): ImportHistoryEntry | null {
  const tenantId = typeof tenant._id === 'string' ? tenant._id : null;
  const fips = resolveTenantFips(tenant);
  if (!tenantId || !fips || String(cacheFile._id) !== fips || cacheFile._deleted) return null;
  const date = typeof cacheFile.updatedAt === 'string'
    ? cacheFile.updatedAt
    : typeof cacheFile.versionDate === 'string' ? cacheFile.versionDate : '';
  if (!date) return null;

  return {
    _id: `geocache-${tenantId}-${fips}-${date}`,
    tenantId,
    tenantName: typeof tenant.departmentName === 'string'
      ? tenant.departmentName
      : typeof tenant.name === 'string' ? tenant.name : null,
    date,
    parcels: typeof cacheFile.featureCount === 'number' ? cacheFile.featureCount : null,
    newCount: null,
    updatesCount: null,
    duration: null,
    version: typeof cacheFile.versionDate === 'string' ? cacheFile.versionDate : null,
    source: 'geocache',
    dataQuality: null,
  };
}

function projectImportRun(doc: Record<string, unknown>): ImportHistoryEntry | null {
  const summary = doc.summary as Record<string, unknown> | undefined;
  const tenantId = typeof doc.tenantId === 'string' ? doc.tenantId : null;
  if (!isParcelImportRun(doc)) return null;

  const dq = summary?.dataQuality as Record<string, unknown> | undefined;
  return {
    _id: String(doc._id),
    tenantId,
    tenantName: typeof doc.tenantName === 'string' ? doc.tenantName : null,
    date: typeof doc.completedAt === 'string' ? doc.completedAt : typeof doc.startedAt === 'string' ? doc.startedAt : String(doc.updatedAt ?? ''),
    parcels: typeof summary?.matchedCount === 'number' ? summary.matchedCount : null,
    newCount: typeof summary?.newCount === 'number' ? summary.newCount : null,
    updatesCount: typeof summary?.updatesCount === 'number' ? summary.updatesCount : null,
    duration: typeof summary?.duration === 'string' ? summary.duration : null,
    version: typeof summary?.version === 'string' ? summary.version : null,
    source: 'import',
    dataQuality: dq ? {
      ownerNamePercent: typeof dq.ownerNamePercent === 'number' ? dq.ownerNamePercent : null,
      mailingAddressPercent: typeof dq.mailingAddressPercent === 'number' ? dq.mailingAddressPercent : null,
      situsAddressPercent: typeof dq.situsAddressPercent === 'number' ? dq.situsAddressPercent : null,
      geometryValidPercent: typeof dq.geometryValidPercent === 'number' ? dq.geometryValidPercent : null,
    } : null,
  };
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe — pure reads, never write)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: `tenants` Dexie table (synced via standard pull pipeline).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 *
 * List all tenants with their update-token entitlement projected from the
 * `updateTokens` field. Suitable for use inside `liveQuery(() => listTenantEntitlements())`.
 * NEVER write to Dexie inside liveQuery.
 */
export async function listTenantEntitlements(): Promise<TenantTokenEntitlement[]> {
  const db = await getSystemDb();
  const docs = (await db.tenants.toArray()) as Array<Record<string, unknown>>;
  return docs
    .filter((d) => !d._deleted)
    .map((d) => {
      const updateTokens = d.updateTokens as Record<string, unknown> | undefined;
      return {
        _id: String(d._id),
        tenantName: String(d.departmentName ?? d.name ?? d._id),
        tokensPerYear: typeof updateTokens?.tokensPerYear === 'number' ? updateTokens.tokensPerYear : 0,
        tokensUsed: typeof updateTokens?.tokensUsed === 'number' ? updateTokens.tokensUsed : 0,
        renewalStart: typeof updateTokens?.renewalStart === 'string' ? updateTokens.renewalStart : null,
        renewalEnd: typeof updateTokens?.renewalEnd === 'string' ? updateTokens.renewalEnd : null,
        currentVersion: typeof updateTokens?.currentVersion === 'string' ? updateTokens.currentVersion : null,
        lastUpdateDate: typeof updateTokens?.lastUpdateDate === 'string' ? updateTokens.lastUpdateDate : null,
      };
    })
    .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
}

/**
 * @localFirst
 * Source of Truth: `geo_cache_files` Dexie table (pulled from the system
 *   `geoCacheFiles` collection via the standard system sync pipeline).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection; the worker updates the server
 *   record and the pull pipeline propagates the change to Dexie.
 * Conflict Resolution: n/a (pure read).
 *
 * List all cached Regrid master-cache GeoParquet files. Suitable for use
 * inside `liveQuery(() => listCacheFiles())`. NEVER write to Dexie inside liveQuery.
 */
export async function listCacheFiles(): Promise<GeoCacheFile[]> {
  const db = await getSystemDb();
  const docs = (await db.geo_cache_files.toArray()) as GeoCacheFile[];
  // Filter out soft-deleted tombstones. Tombstones appear locally after a
  // user-initiated delete (deleteCacheFile writes `_deleted: true`) and
  // arrive from the server via the pull pipeline (G-020 backend soft-delete).
  // Without this filter, deleted rows would reappear in the grid.
  return docs
    .filter((d) => !d._deleted)
    .sort((a, b) => {
      const stateCmp = a.state.localeCompare(b.state);
      if (stateCmp !== 0) return stateCmp;
      return a.county.localeCompare(b.county);
    });
}

/**
 * @localFirst
 * Source of Truth: `import_runs` Dexie table (synced via standard pull pipeline).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 *
 * List recent automated Regrid import runs across all tenants. Suitable for
 * use inside `liveQuery(() => listImportHistory())`. NEVER write to Dexie
 * inside liveQuery.
 */
export async function listImportHistory(): Promise<ImportHistoryEntry[]> {
  const raw = getSystemDexieRaw();
  if (!raw) return [];
  const db = await getSystemDb();
  const [runDocs, tenantDocs, cacheDocs] = await Promise.all([
    raw.table('import_runs').toArray() as Promise<Array<Record<string, unknown>>>,
    db.tenants.toArray() as Promise<Array<Record<string, unknown>>>,
    db.geo_cache_files.toArray() as Promise<Array<Record<string, unknown>>>,
  ]);
  const activeTenants = tenantDocs.filter((d) => !d._deleted);
  const entries = [
    ...runDocs.filter((d) => !d._deleted && d.status === 'completed').map(projectImportRun).filter((entry): entry is ImportHistoryEntry => Boolean(entry)),
    ...activeTenants.map(projectRegridStatistics).filter((entry): entry is ImportHistoryEntry => Boolean(entry)),
    ...activeTenants.flatMap((tenant) => cacheDocs
      .map((cacheFile) => projectCacheFileForTenant(tenant, cacheFile))
      .filter((entry): entry is ImportHistoryEntry => Boolean(entry))),
  ];
  return entries.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 50);
}

/**
 * @localFirst
 * Source of Truth: `geo_cache_files` Dexie table (pulled from the system
 *   `geoCacheFiles` collection via the standard system sync pipeline).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 *
 * Fetch a single Regrid master-cache GeoParquet file by its 5-digit county
 * FIPS code (the `geoCacheFiles._id`). Returns `null` when no file exists for
 * the FIPS. Suitable for use inside
 * `liveQuery(() => getCacheFileByFips(fips))`. NEVER write to Dexie inside
 * liveQuery.
 */
export async function getCacheFileByFips(fips: string): Promise<GeoCacheFile | null> {
  const db = await getSystemDb();
  const doc = (await db.geo_cache_files.get(fips)) as GeoCacheFile | undefined;
  // Treat soft-deleted tombstones as not-found so callers never render a
  // deleted row. Tombstones arrive via the pull pipeline (G-020 backend
  // soft-delete) and from local deleteCacheFile writes.
  if (!doc || doc._deleted) return null;
  return doc;
}

/**
 * @localFirst
 * Source of Truth: `import_runs` Dexie table (synced via standard pull pipeline).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 *
 * List recent automated Regrid import runs for a single tenant, filtered by
 * `tenantId`. Suitable for use inside
 * `liveQuery(() => listImportHistoryForTenant(tenantId))`. NEVER write to
 * Dexie inside liveQuery.
 *
 * IMPORTANT — this deliberately does NOT project the matched geo cache file
 * as a history row (unlike the cross-tenant `listImportHistory`, whose table
 * renders an explicit "GeoCache file" source column). The cache file is the
 * SOURCE FILE, not an import event: it carries the whole-county feature count
 * and file-level quality. The department panel's import-history table has no
 * source column, so a cache-file row there is indistinguishable from a real
 * import — and because the cache file's `updatedAt` is frequently the newest
 * date, it sorted to the front and was read as "the latest import". That is
 * what made the Import Statistics section duplicate the Source File section.
 * The source file already has its own dedicated panel section; it must not
 * masquerade as an import.
 */
export async function listImportHistoryForTenant(tenantId: string): Promise<ImportHistoryEntry[]> {
  const raw = getSystemDexieRaw();
  if (!raw) return [];
  const db = await getSystemDb();
  const [runDocs, tenantDocs] = await Promise.all([
    raw.table('import_runs').toArray() as Promise<Array<Record<string, unknown>>>,
    db.tenants.toArray() as Promise<Array<Record<string, unknown>>>,
  ]);
  const tenant = tenantDocs.find((d) => !d._deleted && String(d._id) === tenantId);
  const entries = [
    ...runDocs
      .filter((d) => !d._deleted
        && (typeof d.tenantId === 'string' ? d.tenantId : null) === tenantId
        && d.status === 'completed')
      .map(projectImportRun)
      .filter((entry): entry is ImportHistoryEntry => Boolean(entry)),
    ...(tenant ? [projectRegridStatistics(tenant)].filter((entry): entry is ImportHistoryEntry => Boolean(entry)) : []),
  ];
  return entries.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 25);
}

/**
 * @process getLastOwnerUpdateRunDate
 * Purpose: Return the newest completed `parcelOwnerUpdate` import run's
 *   freshness claim (`dataAsOf`, falling back to `completedAt`) for a
 *   tenant — the "department-csv" owner-data candidate for the shared
 *   ParcelDataPanel's last-owner-data-update surface (Phase 5c, plan §C.5).
 * Inputs:  tenantId.
 * Outputs: ISO date string or null.
 * Failure Handling: Returns null when no such run exists.
 *
 * @localFirst
 * Source of Truth: `import_runs` Dexie table (synced via standard pull).
 * Write Flow: read-only.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 */
export async function getLastOwnerUpdateRunDate(tenantId: string): Promise<string | null> {
  const raw = getSystemDexieRaw();
  if (!raw) return null;
  const runDocs = await raw.table('import_runs').toArray() as Array<Record<string, unknown>>;
  const dates = runDocs
    .filter((d) => !d._deleted
      && d.dataMode === 'parcelOwnerUpdate'
      && (typeof d.tenantId === 'string' ? d.tenantId : null) === tenantId
      && d.status === 'completed')
    .map((d) => (typeof d.dataAsOf === 'string' ? d.dataAsOf : null)
      ?? (typeof d.completedAt === 'string' ? d.completedAt : null)
      ?? (typeof d.startedAt === 'string' ? d.startedAt : null)
      ?? '')
    .filter((d): d is string => Boolean(d))
    .sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
}

// ---------------------------------------------------------------------------
// Optimistic writes — write an immediate placeholder row to Dexie so the grid
// shows the upload as soon as the user hits Submit, before the server has
// staged the file or created the authoritative geoCacheFiles row.
// NOT liveQuery-safe — call from a submit handler only.
// ---------------------------------------------------------------------------

/**
 * @process writeOptimisticCacheFile
 * Purpose: Write a local `geo_cache_files` placeholder with `status: 'uploading'`
 *          so the grid shows the file immediately. The upload route independently
 *          creates the authoritative MongoDB record before enqueueing the worker.
 *          The worker owns server progress and completion writes.
 * Inputs:  fips, state, stateName, county, filename.
 * Outputs: void — liveQuery re-emits when the Dexie table changes.
 * Failure Handling: Swallows Dexie errors (non-critical; the user can retry).
 * Idempotency: REQUIRED — safe to call for the same FIPS repeatedly.
 *
 * @localFirst
 * Source of Truth: server for persisted lifecycle state; Dexie for immediate UI.
 * Write Flow: service -> local Dexie placeholder; upload route -> MongoDB record.
 * Sync Strategy: pull-only; worker progress/completion replaces the placeholder.
 * Conflict Resolution: server always wins once the upload route responds.
 */
export async function writeOptimisticCacheFile(params: {
  fips: string;
  state: string;
  stateName: string;
  county: string;
  filename: string;
}): Promise<void> {
  try {
    const db = await getSystemDb();
    const now = new Date().toISOString();
    await db.geo_cache_files.put({
      _id: params.fips,
      blobUri: '',
      manifestBlobUri: '',
      filename: params.filename,
      state: params.state,
      stateName: params.stateName,
      county: params.county,
      fipsCode: params.fips,
      sizeBytes: 0,
      versionDate: null,
      dataQuality: null,
      status: 'uploading',
      message: 'Uploading file… 0%',
      progress: { percent: 0, phase: 'browser-upload', stagePercent: 0 },
      __optimistic: true,
      createdAt: now,
      updatedAt: now,
    } as GeoCacheFile);
  } catch (err) {
    console.warn('[parcelDataAdminService] writeOptimisticCacheFile failed:', err);
  }
}

/**
 * @process markOptimisticCacheFileFailed
 * Purpose: Update the `geo_cache_files` row to `status: 'failed'` when the
 *          background upload request fails. This ensures the user sees the
 *          error on the grid row rather than a stuck "uploading" state.
 *          The upload route owns the corresponding authoritative server state.
 * Inputs:  fips, errorMessage.
 * Outputs: void — liveQuery re-emits.
 * Failure Handling: Swallows Dexie errors (non-critical).
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: server; the local failure state only explains a failed request.
 * Write Flow: service -> Dexie put (`status: 'failed'`).
 * Sync Strategy: pull-only; a later server record replaces this local state.
 * Conflict Resolution: server wins.
 */
export async function markOptimisticCacheFileFailed(fips: string, errorMessage: string): Promise<void> {
  try {
    const db = await getSystemDb();
    const now = new Date().toISOString();
    const existing = await db.geo_cache_files.get(fips);
    const base = existing ?? {
      _id: fips,
      blobUri: '',
      manifestBlobUri: '',
      filename: '',
      state: '',
      county: '',
      fipsCode: fips,
      sizeBytes: 0,
      versionDate: null,
      dataQuality: null,
      createdAt: now,
    };
    await db.geo_cache_files.put({
      ...base,
      status: 'failed',
      message: errorMessage,
      error: errorMessage,
      progress: { percent: 100, phase: 'failed' },
      updatedAt: now,
    });
  } catch (err) {
    console.warn('[parcelDataAdminService] markOptimisticCacheFileFailed:', err);
  }
}

/**
 * @process updateOptimisticCacheFileProgress
 * Purpose: Update the progress percent and message on the `geo_cache_files`
 *          row during the file upload phase. Called from the XHR
 *          upload.onprogress handler so the grid row shows a live progress bar
 *          as the file streams to the server. This browser-only state is replaced
 *          by authoritative server progress after the upload route responds.
 * Inputs:  fips, percent (0-100), message.
 * Outputs: void — liveQuery re-emits.
 * Failure Handling: Swallows Dexie errors (non-critical; the next callback
 *          will try again).
 * Idempotency: REQUIRED — safe to call repeatedly with the same or updated values.
 *
 * @localFirst
 * Source of Truth: local during the browser upload; server after submission.
 * Write Flow: service -> Dexie get + put (merge progress fields).
 * Sync Strategy: pull-only; browser upload progress is never pushed.
 * Conflict Resolution: server wins once worker state is available.
 */
export async function updateOptimisticCacheFileProgress(fips: string, percent: number, message: string): Promise<void> {
  try {
    const db = await getSystemDb();
    const existing = await db.geo_cache_files.get(fips);
    if (!existing || existing.__optimistic !== true) return;

    const stagePercent = Math.min(100, Math.max(0, Math.round(percent)));
    const currentStagePercent = existing.progress?.phase === 'browser-upload'
      ? existing.progress.stagePercent ?? 0
      : 0;
    if (stagePercent <= currentStagePercent) return;

    await db.geo_cache_files.put({
      ...existing,
      status: 'uploading',
      message,
      progress: {
        percent: Math.round(stagePercent * 0.14),
        phase: 'browser-upload',
        stagePercent,
      },
      updatedAt: new Date().toISOString(),
    });
  } catch {
    // Non-critical — the progress bar just won't move this tick.
  }
}

/**
 * @process markOptimisticCacheFileQueued
 * Purpose: Advance a browser-upload placeholder to the 14% handoff boundary
 *          after system-api has staged the file and queued worker processing.
 * Inputs:  fips, unique jobId returned by system-api.
 * Outputs: void — liveQuery re-emits.
 * Failure Handling: Swallows Dexie errors; worker sync remains authoritative.
 * Idempotency: REQUIRED — repeated calls preserve the 14% boundary.
 *
 * @localFirst
 * Source of Truth: Dexie for immediate handoff visibility; server for lifecycle state.
 * Write Flow: successful upload response -> local queued marker.
 * Sync Strategy: pull-only; the worker's first 15% update replaces this marker.
 * Conflict Resolution: does nothing once a server row has replaced the optimistic marker.
 */
export async function markOptimisticCacheFileQueued(fips: string, jobId: string): Promise<void> {
  try {
    const db = await getSystemDb();
    const existing = await db.geo_cache_files.get(fips);
    if (!existing || existing.__optimistic !== true) return;

    await db.geo_cache_files.put({
      ...existing,
      status: 'pending',
      message: 'Upload received; queued for processing',
      progress: { percent: 14, phase: 'queued', stagePercent: 100 },
      __uploadJobId: jobId,
      updatedAt: new Date().toISOString(),
    });
  } catch {
    // Non-critical — server pull or worker progress will provide the next state.
  }
}

// ---------------------------------------------------------------------------
// Online refresh — triggers a sync pull for the geoCacheFiles collection.
// NOT liveQuery-safe — call from a separate effect or event handler only.
// ---------------------------------------------------------------------------

/**
 * @process refreshCacheFiles
 * Purpose: Pull the authoritative set of geoCacheFiles rows from system-api
 *          and reconcile the local `geo_cache_files` Dexie table to match
 *          exactly — delete stale rows whose _id is no longer on the server,
 *          preserve active optimistic rows until the server echoes the accepted
 *          upload job id, then put only server rows whose content changed
 *          (key-order-insensitive comparison). This is the on-demand pull that keeps
 *          the grid self-healing without retriggering liveQuery and
 *          synchronization when the server snapshot is unchanged.
 *          Pattern mirrors apiUsageService.refreshUsageSummary.
 * Inputs:  None.
 * Outputs: void — callers re-read via listCacheFiles()/liveQuery, which
 *          re-emits when the Dexie table changes.
 * Failure Handling: Throws on API or Dexie error; caller (useParcelDataAdmin
 *          hook) surfaces the error and leaves the previous cache intact.
 * Idempotency: REQUIRED — a full cache reconcile, safe to call repeatedly.
 *
 * @localFirst
 * Source of Truth: server (system-api `/api/system/cache-upload/files` is
 *   authoritative); `geo_cache_files` is a pull-synced local cache.
 * Write Flow: API fetch → delete stale non-optimistic local rows (whose _id is
 *   no longer in the server response) → put only changed server rows.
 * Sync Strategy: standard pull-synced collection; this function is the
 *   on-demand pull. It never wakes the push loop because these writes came
 *   from the server. The stale-row deletion is local cache reconciliation,
 *   NOT a user-initiated delete.
 * Conflict Resolution: server-authoritative; the pull reconciles any
 *   divergence on the next sync cycle.
 *
 * APPROVED HARD-DELETE EXCEPTION (G-012 — refreshCacheFiles reconciliation):
 * The `db.geo_cache_files.delete(id)` calls in this function are local cache
 * reconciliation, NOT user-initiated deletes. They remove stale local rows
 * whose `_id` is no longer present in the authoritative server response,
 * mirroring the server's state. This is safe because:
 *   1. `geo_cache_files` is server-authoritative — the server is the source
 *      of truth and the local table is a pull-synced cache.
 *   2. The deleted rows no longer exist on the server, so soft-deleting them
 *      locally would leave tombstones that the server will never confirm.
 *   3. Active optimistic rows (in-flight uploads) are preserved until the
 *      server learns about them.
 * This exception is documented per the Phase 1A remediation plan.
 *
 * @logging
 * Events: geo_cache_files_refreshed (info), geo_cache_files_refresh_failed (warn).
 * Level: info/warn
 */
export async function refreshCacheFiles(): Promise<void> {
  try {
    const data = await apiFetch('/api/system/cache-upload/files') as { files: GeoCacheFile[] };
    const files = Array.isArray(data?.files) ? data.files : [];

    const db = await getSystemDb();
    const freshIds = new Set(files.map((f) => f._id));

    // Delete stale local rows whose _id is no longer in the server response.
    // Preserve optimistic upload rows until the server has acknowledged them;
    // otherwise a refresh racing the initial push makes the new grid row vanish.
    const cachedRows = await db.geo_cache_files.toArray();
    const staleIds = cachedRows
      .filter((row) => !freshIds.has(row._id) && row.__optimistic !== true)
      .map((row) => row._id);
    await Promise.all(staleIds.map((id) => db.geo_cache_files.delete(id)));

    // Write only rows whose authoritative server representation changed.
    // Rewriting every row on every poll retriggers liveQuery even when nothing
    // changed, producing visible grid flashes and unnecessary sync activity.
    // Comparison is key-order-insensitive (canonical JSON) so that reordering
    // in the server response does not produce a false positive.
    // Optimistic rows are only overwritten when the server echoes the unique
    // job id returned for this upload attempt; prior-attempt rows cannot clobber
    // the active browser byte progress. A failed optimistic row is terminal and
    // is always replaced by the authoritative server record.
    const cachedById = new Map(cachedRows.map((row) => [row._id, row]));
    const changedFiles = files.filter((file) => {
      const cached = cachedById.get(file._id);
      if (!cached) return true;
      // Preserve an active browser upload until the server snapshot belongs to
      // the newly accepted job. This prevents a previous attempt's row from
      // replacing byte progress during a re-upload, even for the same source file.
      // A failed optimistic row (never received __uploadJobId) is terminal and
      // must accept the server record — otherwise the county is stuck forever.
      // isProtectedOptimisticRow is the shared predicate used by every sync-side
      // optimistic guard, so this on-demand pull cannot drift from the sync path.
      if (isProtectedOptimisticRow(cached)) {
        const acceptedJobId = cached.__uploadJobId;
        if (!acceptedJobId || file.uploadJobId !== acceptedJobId) return false;
      }
      const { fetchedAt: _cachedFetchedAt, __optimistic: _cachedOptimistic, ...cachedServerFields } = cached;
      const { fetchedAt: _serverFetchedAt, __optimistic: _serverOptimistic, ...incomingServerFields } = file;
      return canonicalJsonStringify(cachedServerFields) !== canonicalJsonStringify(incomingServerFields);
    });
    if (changedFiles.length > 0) {
      await db.geo_cache_files.putMany(changedFiles);
    }

    telemetry.trackEvent('geo_cache_files_refreshed', {
      fileCount: String(files.length),
      changedCount: String(changedFiles.length),
      staleRemoved: String(staleIds.length),
    });
  } catch (err) {
    console.error('[parcelDataAdminService] refreshCacheFiles failed:', err);
    telemetry.trackEvent('geo_cache_files_refresh_failed', {});
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'refreshCacheFiles' });
    throw err;
  }
}

/**
 * @process deleteCacheFile
 * Purpose: Soft-delete a geoCacheFiles row by FIPS code via system-api, then
 *          write a local `_deleted: true` tombstone to the Dexie
 *          `geo_cache_files` row and hard-delete any matching
 *          `background_jobs` rows (payload.fips === fips) so liveQuery
 *          re-emits immediately without waiting for the next sync pull.
 * Inputs:  fips — the geoCacheFiles _id (5-digit county FIPS code).
 * Outputs: void.
 * Failure Handling: Throws on API or Dexie error; caller surfaces the error.
 * Idempotency: REQUIRED — deleting a non-existent row is a no-op on the server
 *          (404 is treated as success since the desired state is already reached).
 *
 * @localFirst
 * Source of Truth: server (system-api DELETE `/api/system/cache-upload/files/:fips`
 *   is authoritative — now soft-deletes with `_deleted: true` tombstone, G-020);
 *   `geo_cache_files` is a pull-synced local cache (push blocked — the worker
 *   is the only writer).
 * Write Flow: API DELETE (server soft-delete) → local `db.geo_cache_files.put`
 *   with `_deleted: true` tombstone → local `db.background_jobs.bulkDelete(matchingJobIds)`.
 * Sync Strategy: the local tombstone hides the row immediately; the server
 *   DELETE and subsequent pull are authoritative. A 404 is treated as success.
 * Conflict Resolution: the server DELETE is authoritative; a 404 is treated
 *   as success.
 *
 * G-020 SOFT-DELETE CONVERSION (Phase 1B):
 * The backend DELETE route (`cacheUploadRoutes.ts`) now soft-deletes the
 * `geoCacheFiles` row with `_deleted: true`, `deletedBy`, `deletedAt`. This
 * frontend function mirrors that with a local tombstone `put` so the row
 * disappears from the grid immediately and the next sync pull confirms the
 * tombstone. The `background_jobs` rows are hard-deleted locally (transient
 * job records, not a synced collection — matches the backend's documented
 * hard-delete exception for `backgroundJobs`).
 *
 * @logging
 * Events: geo_cache_file_deleted (info), geo_cache_file_delete_failed (warn).
 * Level: info/warn
 */
export async function deleteCacheFile(fips: string): Promise<void> {
  try {
    // Call the API first (server-authoritative soft-delete). A 404 is
    // acceptable — the row may have already been deleted by another admin or
    // a sync cycle. apiFetch throws an Error with the server's error message;
    // a 404 from this route returns "Cache file not found".
    try {
      await apiFetch(`/api/system/cache-upload/files/${fips}`, { method: 'DELETE' });
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : String(err);
      // If the row is already gone (404), proceed with local cleanup.
      if (!/not found|404/i.test(msg)) throw err;
    }

    const db = await getSystemDb();

    // Write a local `_deleted: true` tombstone so the row disappears from the
    // grid immediately. The preceding API call owns the server tombstone. Read
    // the existing row first to preserve its metadata locally.
    const existing = await db.geo_cache_files.get(fips);
    const now = new Date().toISOString();
    await db.geo_cache_files.put({
      ...(existing as GeoCacheFile | undefined),
      _id: fips,
      _deleted: true,
      deletedAt: now,
      deletedBy: 'system-web',
      // Clear the optimistic marker on the tombstone. A delete is an explicit
      // user intent, so the row must NOT be protected by the optimistic-row
      // tombstone guard in the sync delta-pull handler — otherwise the server's
      // confirming tombstone would be preserved instead of applied and the row
      // would linger in Dexie forever.
      __optimistic: undefined,
      updatedAt: now,
    } as GeoCacheFile);
    // Hard-delete any background_jobs rows whose payload.fips === fips.
    // `background_jobs` are transient job records (not a synced collection);
    // the backend hard-deletes them too (documented exception in
    // cacheUploadRoutes.ts DELETE). Use the jobType index to narrow the scan
    // to cache-upload jobs only (the only job type that carries payload.fips)
    // instead of loading all jobs.
    const raw = getSystemDexieRaw();
    if (raw) {
      const matchingJobs = await raw
        .table('background_jobs')
        .where('jobType')
        .equals('cacheUpload')
        .filter((j: Record<string, any>) => j?.payload?.fips === fips)
        .toArray();
      const jobIdsToDelete = matchingJobs.map((j: Record<string, any>) => j._id);
      if (jobIdsToDelete.length > 0) {
        await raw.table('background_jobs').bulkDelete(jobIdsToDelete);
        // G-015: wake sync push loop after write-through. No-op for
        // server-authoritative `background_jobs` (no push loop registered).
        wakeLocalWrite('background_jobs');
      }
    }

    telemetry.trackEvent('geo_cache_file_deleted', { fips });
  } catch (err) {
    console.error('[parcelDataAdminService] deleteCacheFile failed:', err);
    telemetry.trackEvent('geo_cache_file_delete_failed', { fips });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'deleteCacheFile', fips });
    throw err;
  }
}

/**
 * @process setTenantTokenEntitlement
 * Purpose: Update a tenant's update-token entitlement via the Phase 8 API route
 *          (PUT /api/system/departments/:id/update-tokens) and write the result
 *          through to Dexie so liveQuery re-emits. Write-through: the API call
 *          is the source of truth; the Dexie put is an optimistic cache update
 *          that the next pull cycle reconciles.
 * Inputs:  tenantId, tokensPerYear.
 * Outputs: void.
 * Failure Handling: Throws on API or Dexie write failure. The UI surfaces the
 *          error and leaves the previous cache intact (the API call failed, so
 *          no Dexie write occurs).
 * Idempotency: REQUIRED — a full entitlement field replace.
 *
 * @localFirst
 * Source of Truth: system-api (server-authoritative for `tenants` collection;
 *   the `updateTokens` field is persisted by Phase 8 routes).
 * Write Flow: service -> apiFetch (PUT) -> Dexie put (optimistic cache update)
 *   -> wakeLocalWrite (`tenants` IS push-allowed, so this is REQUIRED to
 *   propagate the local edit to the server). liveQuery re-emits.
 * Sync Strategy: write-through; the next pull cycle reconciles any divergence.
 * Conflict Resolution: server-authoritative; the pull reconciles any
 *   divergence on the next sync cycle.
 *
 * @logging
 * Events: tenant_token_entitlement_saved (info), tenant_token_entitlement_save_failed (warn).
 * Level: info/warn
 */
export async function setTenantTokenEntitlement(tenantId: string, tokensPerYear: number): Promise<void> {
  try {
    // Write-through: call the API first (server-authoritative), then update Dexie.
    const updated = await apiFetch(`/api/system/departments/${tenantId}/update-tokens`, {
      method: 'PUT',
      body: JSON.stringify({ tokensPerYear }),
    }) as Record<string, unknown>;

    const db = await getSystemDb();
    const existing = (await db.tenants.get(tenantId)) as Record<string, unknown> | undefined;
    if (!existing) {
      throw new Error(`Tenant ${tenantId} not found in local cache`);
    }
    await db.tenants.put({
      ...existing,
      updateTokens: updated,
    } as any);
    // G-015: wake sync push loop after write-through. `tenants` IS push-allowed
    // in SYSTEM_PUSH_ALLOWED_COLLECTIONS, so this wake is REQUIRED to propagate
    // the local edit to the server.
    wakeLocalWrite('tenants');
    telemetry.trackEvent('tenant_token_entitlement_saved', { tenantId, tokensPerYear: String(tokensPerYear) });
  } catch (err) {
    console.error('[parcelDataAdminService] setTenantTokenEntitlement failed:', err);
    telemetry.trackEvent('tenant_token_entitlement_save_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'setTenantTokenEntitlement', tenantId });
    throw err;
  }
}

/**
 * @process adminBypassUpdate
 * Purpose: Trigger an admin parcel data update for a tenant WITHOUT consuming
 *          a token. Calls POST /api/system/departments/:id/update-tokens/admin-update
 *          and writes the result through to Dexie so liveQuery re-emits.
 *          Admin updates bypass token consumption (approved decision).
 * Inputs:  tenantId, newVersion — the Regrid file version the department is
 *          being moved to (ISO date string).
 * Outputs: void.
 * Failure Handling: Throws on API or Dexie write failure.
 * Idempotency: REQUIRED — repeated calls with the same version are a no-op on
 *          `currentVersion` (only `lastUpdateDate` advances).
 *
 * @localFirst
 * Source of Truth: system-api (server-authoritative for `tenants` collection;
 *   the `updateTokens` field is persisted by the admin-update route).
 * Write Flow: service -> apiFetch (POST) -> Dexie put (optimistic cache update)
 *   -> wakeLocalWrite (`tenants` IS push-allowed, so this is REQUIRED to
 *   propagate the local edit to the server). liveQuery re-emits.
 * Sync Strategy: write-through; the next pull cycle reconciles any divergence.
 * Conflict Resolution: server-authoritative; the pull reconciles any
 *   divergence on the next sync cycle.
 *
 * @logging
 * Events: tenant_admin_bypass_update_saved (info), tenant_admin_bypass_update_failed (warn).
 * Level: info/warn
 */
export async function adminBypassUpdate(tenantId: string, newVersion: string): Promise<void> {
  try {
    const updated = await apiFetch(`/api/system/departments/${tenantId}/update-tokens/admin-update`, {
      method: 'POST',
      body: JSON.stringify({ newVersion }),
    }) as Record<string, unknown>;

    const db = await getSystemDb();
    const existing = (await db.tenants.get(tenantId)) as Record<string, unknown> | undefined;
    if (!existing) {
      throw new Error(`Tenant ${tenantId} not found in local cache`);
    }
    await db.tenants.put({
      ...existing,
      updateTokens: updated,
    } as any);
    // G-015: wake sync push loop after write-through. `tenants` IS push-allowed
    // in SYSTEM_PUSH_ALLOWED_COLLECTIONS, so this wake is REQUIRED.
    wakeLocalWrite('tenants');
    telemetry.trackEvent('tenant_admin_bypass_update_saved', { tenantId, newVersion });
  } catch (err) {
    console.error('[parcelDataAdminService] adminBypassUpdate failed:', err);
    telemetry.trackEvent('tenant_admin_bypass_update_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'adminBypassUpdate', tenantId });
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Parcel configuration save + admin-triggered import (Phase 9+)
// ---------------------------------------------------------------------------

/**
 * @contract ParcelImportConfig
 * Purpose: The parcel data configuration saved to `tenant.parcelImport` +
 *          `tenant.updateTokens` from the admin DepartmentParcelDataTab.
 * Fields:  path — 'regrid' | 'manual' | 'skip'; regridFileId — 5-digit FIPS;
 *          regridFileName — display name; filterMode — 'all' | 'department' |
 *          'fireHazard'; hazardLevels — selected hazard levels;
 *          createOwnerContacts — primary-owner contact materialization policy;
 *          tokensPerYear.
 * Rules:   When path is 'regrid', regridFileId must be a 5-digit FIPS and
 *          tokensPerYear defaults to 1. createOwnerContacts defaults to false
 *          — contact creation is an explicit opt-in department policy, never
 *          an implicit side effect of a parcel import.
 * Sync:    Server-authoritative (system DB `tenants.parcelImport` +
 *          `tenants.updateTokens`); pull-synced to system-web Dexie.
 */
export interface ParcelImportConfig {
  path: 'regrid' | 'manual' | 'skip';
  regridFileId?: string;
  regridFileName?: string;
  filterMode?: 'all' | 'department' | 'fireHazard';
  hazardLevels?: string[];
  createOwnerContacts?: boolean;
  tokensPerYear?: number;
}

/**
 * @process saveParcelConfig
 * Purpose: Save parcel data configuration (parcelImport + updateTokens) on
 *          the tenant document from the admin DepartmentParcelDataTab. This
 *          is the admin-side equivalent of onboarding Step 9 — it lets an
 *          admin configure or reconfigure parcel data for a department that
 *          was not configured during onboarding (or needs a change).
 *          Write-through: PUT /api/system/departments/:id/parcel-config →
 *          Dexie put so liveQuery re-emits immediately.
 * Inputs:  tenantId, config (ParcelImportConfig).
 * Outputs: void — liveQuery re-emits when the Dexie tenant record changes.
 * Failure Handling: Throws on API or Dexie write failure.
 * Idempotency: REQUIRED — a full parcelImport field replace.
 *
 * @localFirst
 * Source of Truth: system-api (server-authoritative for `tenants` collection).
 * Write Flow: service -> apiFetch (PUT) -> Dexie put; liveQuery re-emits.
 * Sync Strategy: write-through; the next pull cycle reconciles any divergence.
 *
 * @logging
 * Events: parcel_config_saved (info), parcel_config_save_failed (warn).
 * Level: info/warn
 */
export async function saveParcelConfig(tenantId: string, config: ParcelImportConfig): Promise<void> {
  try {
    const result = await apiFetch(`/api/system/departments/${tenantId}/parcel-config`, {
      method: 'PUT',
      body: JSON.stringify({
        parcelImport: {
          path: config.path,
          ...(config.regridFileId != null ? { regridFileId: config.regridFileId } : {}),
          ...(config.regridFileName != null ? { regridFileName: config.regridFileName } : {}),
          ...(config.filterMode != null ? { filterMode: config.filterMode } : {}),
          ...(config.hazardLevels != null ? { hazardLevels: config.hazardLevels } : {}),
          ...(config.createOwnerContacts != null ? { createOwnerContacts: config.createOwnerContacts } : {}),
        },
        ...(config.tokensPerYear != null ? { tokensPerYear: config.tokensPerYear } : {}),
      }),
    }) as { parcelImport?: Record<string, unknown>; updateTokens?: Record<string, unknown> };

    // Write-through to Dexie so liveQuery re-emits immediately
    const db = await getSystemDb();
    const existing = (await db.tenants.get(tenantId)) as Record<string, unknown> | undefined;
    if (!existing) {
      throw new Error(`Tenant ${tenantId} not found in local cache`);
    }
    await db.tenants.put({
      ...existing,
      ...(result.parcelImport ? { parcelImport: result.parcelImport } : {}),
      ...(result.updateTokens ? { updateTokens: result.updateTokens } : {}),
      updatedAt: new Date().toISOString(),
    } as any);
    // G-015: wake sync push loop after write-through. `tenants` IS push-allowed
    // in SYSTEM_PUSH_ALLOWED_COLLECTIONS, so this wake is REQUIRED.
    wakeLocalWrite('tenants');

    telemetry.trackEvent('parcel_config_saved', {
      tenantId,
      path: config.path,
    });
  } catch (err) {
    console.error('[parcelDataAdminService] saveParcelConfig failed:', err);
    telemetry.trackEvent('parcel_config_save_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'saveParcelConfig', tenantId });
    throw err;
  }
}

/**
 * @contract TriggerImportResult
 * Purpose: Response from the trigger-import endpoint.
 * Fields:  jobId — the backgroundJobs _id for progress tracking; status —
 *          'queued'; filterMode — the resolved filter mode used.
 * Rules:   The jobId can be used to subscribe to the backgroundJobs Dexie
 *          table for live progress updates.
 * Sync:    The backgroundJobs row is pull-synced to system-web Dexie.
 */
export interface TriggerImportResult {
  jobId: string;
  status: 'queued';
  filterMode: 'all' | 'department' | 'fireHazard';
}

/**
 * @process triggerRegridImport
 * Purpose: Trigger a Regrid parcel import for a department WITHOUT consuming
 *          a token (admin bypass). Calls
 *          POST /api/system/departments/:id/parcel-config/trigger-import and
 *          returns the background job ID for progress tracking. The job
 *          progress is tracked via the `background_jobs` Dexie table
 *          (pull-synced), which the UI can subscribe to with liveQuery.
 * Inputs:  tenantId, optional overrides for filterMode, hazardLevels, and
 *          createOwnerContacts. Omitted overrides fall back to the saved
 *          department parcel configuration.
 * Outputs: TriggerImportResult with the jobId.
 * Failure Handling: Throws on API failure (400/404/503/500).
 * Idempotency: NOT REQUIRED — each call enqueues a new import job.
 *
 * @localFirst
 * Source of Truth: system-api (server-authoritative for job enqueue).
 * Write Flow: service -> apiFetch (POST) -> returns jobId; an optimistic
 *   placeholder is written to the background_jobs Dexie table so the UI
 *   shows immediate feedback before the sync pull delivers the full server
 *   document. The sync pull overwrites the placeholder with the authoritative
 *   server version.
 * Sync Strategy: pull-synced; the UI subscribes to background_jobs via
 *   liveQuery for live progress. background_jobs is NOT in
 *   SYSTEM_PUSH_ALLOWED_COLLECTIONS — the optimistic write is local-only.
 *
 * @logging
 * Events: parcel_config_import_triggered (info), parcel_config_import_trigger_failed (warn).
 * Level: info/warn
 */
export async function triggerRegridImport(
  tenantId: string,
  overrides?: {
    filterMode?: 'all' | 'department' | 'fireHazard';
    hazardLevels?: string[];
    createOwnerContacts?: boolean;
  },
): Promise<TriggerImportResult> {
  try {
    const result = await apiFetch(`/api/system/departments/${tenantId}/parcel-config/trigger-import`, {
      method: 'POST',
      body: JSON.stringify(overrides ?? {}),
    }) as TriggerImportResult;

    // Optimistic Dexie write — insert a minimal placeholder job document so
    // the liveQuery in useDepartmentParcelData picks it up immediately,
    // before the sync pull delivers the full server document. This gives
    // the user instant visual feedback (ImportProgressDisplay renders with
    // "Queued" status). The sync pull will overwrite this placeholder with
    // the authoritative server version (including progress, message, etc.).
    // background_jobs is server-authoritative (not push-allowed), so this
    // local write will never be pushed to the server.
    try {
      const db = await getSystemDb();
      const now = new Date().toISOString();
      await db.background_jobs.put({
        _id: result.jobId,
        jobType: 'regridIngestion',
        scope: 'system',
        tenantId,
        status: 'pending',
        title: 'Regrid Parcel Import',
        message: 'Queued — waiting for worker to pick up…',
        progress: { percent: 0, current: 0, total: 0, phase: 'queued' },
        startedAt: now,
        updatedAt: now,
      } as any);
      // G-015: wake sync push loop after write-through. No-op for
      // server-authoritative `background_jobs` (no push loop registered).
      wakeLocalWrite('background_jobs');
    } catch (optimisticErr) {
      // Non-fatal — the sync pull will deliver the real document shortly.
      console.warn('[parcelDataAdminService] Optimistic background_jobs write failed (non-fatal):', optimisticErr);
    }

    telemetry.trackEvent('parcel_config_import_triggered', {
      tenantId,
      jobId: result.jobId,
      filterMode: result.filterMode,
    });

    return result;
  } catch (err) {
    console.error('[parcelDataAdminService] triggerRegridImport failed:', err);
    telemetry.trackEvent('parcel_config_import_trigger_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'triggerRegridImport', tenantId });
    throw err;
  }
}

/**
 * @process materializeOwnerContacts
 * Steps: enqueue the contacts-only worker job over imported Regrid parcels.
 * Inputs: tenantId.
 * Outputs: background job id.
 * Failure Handling: propagates API errors to the caller.
 * Idempotency: REQUIRED — the server uses a stable tenant-scoped job key.
 * @localFirst Source of Truth: server for this inherently-online background operation.
 * Write Flow: service calls the admin route; backgroundJobs sync back through Dexie.
 * Sync Strategy: pull-synced job progress.
 * Conflict Resolution: server idempotency result is authoritative.
 */
export async function materializeOwnerContacts(tenantId: string): Promise<{ jobId: string; status: 'queued' }> {
  return apiFetch(`/api/system/departments/${tenantId}/parcel-config/materialize-owner-contacts`, { method: 'POST' });
}

/**
 * @contract CancelImportResult
 * Purpose: Response from the cancel-import endpoint.
 * Fields:  jobId — the backgroundJobs _id; status — the resulting terminal status.
 * Rules:   An active job is marked as `cancelled` (not deleted); an already
 *          terminal job keeps its existing status so the audit trail is preserved.
 * Sync:    The backgroundJobs row update is pull-synced to system-web Dexie.
 */
export interface CancelImportResult {
  jobId: string;
  status: 'cancelled' | 'completed' | 'failed';
}

/**
 * @process cancelRegridImport
 * Purpose: Cancel a stuck/queued Regrid parcel import job for a department.
 *          Calls POST /api/system/departments/:id/parcel-config/cancel-import
 *          and optionally updates the local Dexie `background_jobs` row so the
 *          spinner disappears immediately without waiting for the next sync
 *          pull. When `jobId` is omitted, the server cancels the most recent
 *          pending/running regridIngestion job for the tenant.
 * Inputs:  tenantId, optional jobId (specific job to cancel).
 * Outputs: CancelImportResult with the jobId and resulting terminal status.
 * Failure Handling: Throws on API failure (400/500).
 * Idempotency: REQUIRED — cancelling an already-terminal job returns its
 *          existing terminal status without changing the job.
 *
 * @localFirst
 * Source of Truth: system-api (server-authoritative for backgroundJobs).
 * Write Flow: service -> apiFetch (POST) -> optimistic Dexie put on the
 *   background_jobs row so liveQuery re-emits immediately.
 * Sync Strategy: write-through; the next pull cycle reconciles any divergence.
 *
 * @logging
 * Events: parcel_config_import_cancelled (info), parcel_config_import_cancel_failed (warn).
 * Level: info/warn
 */
export async function cancelRegridImport(
  tenantId: string,
  jobId?: string,
): Promise<CancelImportResult> {
  let result: CancelImportResult;
  try {
    result = await apiFetch(`/api/system/departments/${tenantId}/parcel-config/cancel-import`, {
      method: 'POST',
      body: JSON.stringify(jobId ? { jobId } : {}),
    }) as CancelImportResult;
  } catch (err) {
    console.error('[parcelDataAdminService] cancelRegridImport failed:', err);
    telemetry.trackEvent('parcel_config_import_cancel_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'cancelRegridImport', tenantId });
    throw err;
  }

  // Optimistically update the local Dexie background_jobs row so the
  // spinner disappears immediately without waiting for the next sync pull.
  // This is critical when the local row is a stale 'pending' placeholder
  // that hasn't been reconciled by sync pull yet (e.g. the worker failed
  // before the sync push delivered the terminal status).
  const raw = getSystemDexieRaw();
  if (raw && result.jobId) {
    try {
      const existing = await raw.table('background_jobs').get(result.jobId) as Record<string, unknown> | undefined;
      if (existing) {
        const now = new Date().toISOString();
        await raw.table('background_jobs').put({
          ...existing,
          status: result.status,
          message: result.status === 'cancelled' ? 'Cancelled by system admin' : existing.message,
          completedAt: now,
          updatedAt: now,
        });
        // G-015: wake sync push loop after write-through. No-op for
        // server-authoritative `background_jobs` (no push loop registered).
        wakeLocalWrite('background_jobs');
      }
    } catch (dexieErr) {
      // Non-critical — the sync pull will reconcile the row shortly.
      console.warn('[parcelDataAdminService] Optimistic Dexie cancel update failed (non-critical):', dexieErr);
    }
  }

  telemetry.trackEvent('parcel_config_import_cancelled', {
    tenantId,
    jobId: result.jobId,
  });

  return result;
}

// ---------------------------------------------------------------------------
// Parcel count estimate (Phase 9+ — admin pre-import estimate)
// ---------------------------------------------------------------------------

/**
 * @contract ParcelEstimateResult
 * Purpose: Response from the estimate-parcels endpoint.
 * Fields:  matchedCount — estimated parcels within the department boundary;
 *          sourceCount — total parcels in the GeoParquet file.
 * Rules:   matchedCount is an approximate count based on a spatial
 *          ST_Intersects query against the department boundary polygon.
 * Sync:    Backend-only transient response; no persistence.
 */
export interface ParcelEstimateResult {
  matchedCount: number;
  sourceCount: number;
}

/**
 * @process estimateParcels
 * Purpose: Estimate the number of parcels within the department's boundary
 *          from the matched Regrid GeoParquet cache file, WITHOUT running a
 *          full import. Calls
 *          POST /api/system/departments/:id/parcel-config/estimate-parcels
 *          which downloads the GeoParquet blob, runs a DuckDB COUNT with
 *          ST_Intersects against the department boundary, and returns the
 *          matched + source counts.
 * Inputs:  tenantId.
 * Outputs: ParcelEstimateResult with matchedCount and sourceCount.
 * Failure Handling: Throws on API failure (400/404/500).
 * Idempotency: REQUIRED — read-only operation; safe to call repeatedly.
 *
 * @localFirst
 * Source of Truth: system-api (server-side DuckDB query against the blob).
 * Write Flow: service -> apiFetch (POST) -> returns counts; no Dexie write.
 * Sync Strategy: transient online-only call; no local persistence.
 *
 * @logging
 * Events: parcel_config_estimate_requested (info),
 *         parcel_config_estimate_failed (warn).
 * Level: info/warn
 */
export async function estimateParcels(tenantId: string): Promise<ParcelEstimateResult> {
  try {
    const result = await apiFetch(`/api/system/departments/${tenantId}/parcel-config/estimate-parcels`, {
      method: 'POST',
      body: JSON.stringify({}),
    }) as ParcelEstimateResult;

    telemetry.trackEvent('parcel_config_estimate_requested', {
      tenantId,
      matchedCount: String(result.matchedCount),
      sourceCount: String(result.sourceCount),
    });

    return result;
  } catch (err) {
    console.error('[parcelDataAdminService] estimateParcels failed:', err);
    telemetry.trackEvent('parcel_config_estimate_failed', { tenantId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'estimateParcels', tenantId });
    throw err;
  }
}
